import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

export interface ParticipantStatsHeaderProps {
  /** Total predictions submitted by the participant. */
  totalPredictions: number;
  /** Number of predictions where the outcome was correct (incl. exact). */
  correctPredictions: number;
  /** Total tournament points — shown in the card subtitle. */
  totalPoints: number;
  /** Distribution counts for the per-bucket bars. */
  distribution: {
    exact: number;   // exact-score hits
    outcome: number; // correct outcome, exact-score hits included
    wrong: number;   // finished, no points
    pending: number; // not yet finished (or no result yet)
  };
}

/**
 * Stats summary card on the participant statistics page — "accuracy" panel:
 *   • target icon + "Accuracy" headline + "{N} predictions · {pts} pts" subtitle
 *   • big accuracy-percent metric
 *   • four per-bucket progress bars (exact / outcome / wrong / pending)
 *
 * Renders in the desktop sidebar and (on mobile) inline at the top of the main
 * column. The identity + hero points live in MainSection's header, so this card
 * only carries the analytical breakdown.
 */
export const ParticipantStatsHeader: React.FC<ParticipantStatsHeaderProps> = ({
  totalPredictions,
  correctPredictions,
  totalPoints,
  distribution,
}) => {
  const { t } = useTranslation(['tournament']);

  const accuracyPercent = totalPredictions > 0
    ? Math.round((correctPredictions / totalPredictions) * 100)
    : 0;

  // Bucket fill widths are relative to all predictions. `outcome`, `wrong` and
  // `pending` partition them (`exact` is a subset of `outcome`); fall back to
  // totalPredictions if the sum is empty, and guard divide-by-zero.
  const bucketTotal = distribution.outcome + distribution.wrong + distribution.pending;
  const denom = bucketTotal || totalPredictions || 1;

  const buckets: Array<{ key: string; count: number; fillClass: string }> = [
    { key: 'exact',   count: distribution.exact,   fillClass: styles.fillExact },
    { key: 'outcome', count: distribution.outcome, fillClass: styles.fillOutcome },
    { key: 'wrong',   count: distribution.wrong,   fillClass: styles.fillWrong },
    { key: 'pending', count: distribution.pending, fillClass: styles.fillPending },
  ];

  return (
    <article className={styles.card}>
      {/* ─── Header — title + subtitle ─────────────────────── */}
      <div className={styles.headText}>
        <h3 className={styles.title}>
          {t('tournament:participantStats.kpi.accuracy')}
        </h3>
        <span className={styles.subtitle}>
          {t('tournament:participantStats.liga.subtitle', {
            count: totalPredictions,
            points: totalPoints.toFixed(2),
          })}
        </span>
      </div>

      {/* ─── Headline metric — accuracy percent ────────────── */}
      <div className={styles.metric}>
        <span className={styles.metricValue}>{accuracyPercent}</span>
        <span className={styles.metricUnit}>%</span>
      </div>

      {/* ─── Per-bucket progress bars ──────────────────────── */}
      <div className={styles.bars}>
        {buckets.map(({ key, count, fillClass }) => {
          const widthPercent = Math.round((count / denom) * 100);
          return (
            <div className={styles.barRow} key={key}>
              <div className={styles.barHeader}>
                <span className={styles.barLabel}>
                  {t(`tournament:participantStats.distribution.${key}`)}
                </span>
                <span className={styles.barCount}>{count}</span>
              </div>
              <div className={styles.barTrack}>
                <div
                  className={`${styles.barFill} ${fillClass}`}
                  style={{ width: `${widthPercent}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </article>
  );
};

export default ParticipantStatsHeader;
